/**
 * @fileoverview Cortex Real-time Agent Service - Phase 4 Compliant
 * @module services/cortex/realtime-agent
 * @description Handles WebSocket connections and voice streaming with ElevenLabs Conversational AI.
 *
 * @implements {ICortexRealtimeAgent}
 * @implements {IService}
 * @service-registry
 * **Registry Configuration:**
 * - Key: 'cortexRealtimeAgent'
 * - Factory: `() => cortexRealtimeAgent`
 * - Dependencies: []
 * - Lifecycle: singleton
 *
 * @architecture Phase 4 compliant service
 * @version 1.0.0 - Phase 4 implementation
 * @since 1.0.0 - Modular architecture implementation
 */
import type { ICortexRealtimeAgent, AgentConfig, ConversationSession, CortexEvent, } from './types';
import type { IService, ServiceResponse } from '../service-registry';
import { logger } from "../../lib/logger";
// Environment variable access
const getEnvVar = (key: string): string => {
    if (typeof window !== 'undefined') {
        return ((window as unknown as Record<string, Record<string, string>>).__ENV__?.[key]) ||
            ((import.meta as unknown as Record<string, Record<string, string>>).env?.[key]) || '';
    }
    if (typeof process !== 'undefined') {
        return process.env?.[key] || '';
    }
    return '';
};
/**
 * Get ElevenLabs configuration
 */
function getElevenLabsConfig(): {
    apiKey?: string;
    agentId?: string;
} {
    const apiKey = getEnvVar('VITE_ELEVENLABS_API_KEY') || undefined;
    const agentId = getEnvVar('VITE_ELEVENLABS_AGENT_ID') || undefined;
    return { apiKey, agentId };
}
/**
 * Cortex Real-time Agent Service
 * Phase 4 compliant service for managing voice conversations with ElevenLabs
 */
class CortexRealtimeAgentService implements ICortexRealtimeAgent {
    readonly name = 'cortexRealtimeAgent' as const;
    private config: AgentConfig;
    private session: ConversationSession | null = null;
    private audioContext: AudioContext | null = null;
    private mediaStream: MediaStream | null = null;
    private websocket: WebSocket | null = null;
    private webrtcConnection: RTCPeerConnection | null = null;
    private isConnected = false;
    private eventListeners = new Map<string, Set<(data: unknown) => void>>();
    // Default configuration for Cortex
    private defaultConfig: AgentConfig = {
        voiceId: 'JBFqnCBsd6RMkjVDRZzb',
        modelId: 'eleven_multilingual_v2',
        language: 'en',
        temperature: 0.7,
        maxTokens: 2048,
        systemPrompt: `You are Cortex, an advanced AI assistant specializing in business automation, workflow optimization, and intelligent task management.

Your core capabilities include:
- Real-time voice conversations with natural, conversational responses
- Business process automation consulting and recommendations
- Workflow analysis and optimization strategies
- Task management and productivity enhancement
- Tool integration for practical business solutions
- ROI calculations and cost-benefit analysis
- Integration recommendations for software and services

Formatting Guidelines:
- Use emojis to make responses more engaging and visually appealing
- Use headings (##, ###) to organize information into clear sections
- Use bullet points and numbered lists for structured information
- Use bold text (**text**) for emphasis on key points
- Use code blocks for technical examples or commands
- Use blockquotes (> ) for important notes or warnings
- Use tables for comparing data or showing structured information
- Use horizontal rules (---) to separate major sections
- Keep paragraphs short and scannable (2-3 sentences max)
- Use consistent emoji patterns for different types of content (e.g., ✅ for completed tasks, ⚠️ for warnings, 💡 for tips)

Guidelines for conversation:
- Always respond naturally and conversationally, as if speaking to a colleague
- Use available tools proactively when they would be helpful
- Ask clarifying questions when needed to provide better assistance
- Provide specific, actionable recommendations
- Maintain context throughout the conversation
- Be proactive in offering relevant tools or capabilities
- Make your responses visually appealing and easy to scan

Current context: You are having a real-time voice conversation with a business professional interested in automation solutions.`,
    };
    constructor(config: Partial<AgentConfig> = {}) {
        const elevenLabsConfig = getElevenLabsConfig();
        this.config = {
            ...this.defaultConfig,
            agentId: elevenLabsConfig.agentId,
            ...config,
        };
        logger.info('🎭 Cortex Real-time Agent Service initialized (Phase 4)');
    }
    /**
     * Initialize the service
     */
    async initialize(): Promise<void> {
        logger.info('🚀 Initializing Cortex Real-time Agent Service...');
        // Service is ready to use
    }
    /**
     * Dispose the service and clean up resources
     */
    async dispose(): Promise<void> {
        logger.info('🧹 Disposing Cortex Real-time Agent Service...');
        await this.endSession();
        this.eventListeners.clear();
    }
    /**
     * Start a voice conversation session
     */
    async startSession(useWebRTC = false): Promise<ServiceResponse<string>> {
        try {
            logger.info('🎤 Starting Cortex session...');
            if (!this.config.agentId) {
                return { success: false, error: 'Agent ID is required to start conversation' };
            }
            // Initialize audio context
            await this.initializeAudio();
            // Generate session ID
            const sessionId = `cortex_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const elevenLabsConfig = getElevenLabsConfig();
            let wsUrl: string;
            let conversationId: string | undefined;
            // Validate API key
            if (!elevenLabsConfig.apiKey) {
                logger.warn('⚠️ ElevenLabs API key not configured. Using fallback WebSocket URL.');
                wsUrl = `wss://api.elevenlabs.io/v1/convai/conversation?agent_id=${this.config.agentId}`;
            }
            else {
                // Try to get signed URL for private agents
                try {
                    const response = await fetch(`https://api.elevenlabs.io/v1/convai/conversation/get-signed-url?agent_id=${this.config.agentId}`, {
                        method: 'GET',
                        headers: {
                            'xi-api-key': elevenLabsConfig.apiKey,
                            'Content-Type': 'application/json',
                        },
                    });
                    if (response.ok) {
                        const data = (await response.json()) as {
                            signed_url: string;
                            conversation_id?: string;
                        };
                        wsUrl = data.signed_url;
                        conversationId = data.conversation_id;
                    }
                    else {
                        logger.warn(`⚠️ Failed to get signed URL (${response.status}). Using fallback.`);
                        wsUrl = `wss://api.elevenlabs.io/v1/convai/conversation?agent_id=${this.config.agentId}`;
                    }
                }
                catch (error) {
                    logger.warn('⚠️ Error fetching signed URL. Using fallback.', error);
                    wsUrl = `wss://api.elevenlabs.io/v1/convai/conversation?agent_id=${this.config.agentId}`;
                }
            }
            this.session = {
                sessionId,
                conversationId,
                isActive: true,
                startTime: new Date(),
                messageCount: 0,
                connectionType: useWebRTC ? 'webrtc' : 'websocket',
                signedUrl: wsUrl,
            };
            // Initialize WebSocket connection
            await this.initializeVoiceWebSocket(wsUrl, elevenLabsConfig.apiKey || '');
            logger.info('✅ Cortex session started:', sessionId);
            return { success: true, data: sessionId };
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to start session';
            logger.error('❌ Failed to start session:', error);
            await this.endSession();
            return { success: false, error: errorMessage };
        }
    }
    /**
     * End the current conversation session
     */
    async endSession(): Promise<ServiceResponse<void>> {
        try {
            logger.info('🎤 Ending Cortex session...');
            this.isConnected = false;
            if (this.session) {
                this.session.isActive = false;
                logger.info('📊 Session summary:', {
                    duration: Date.now() - this.session.startTime.getTime(),
                    messages: this.session.messageCount,
                    connectionType: this.session.connectionType,
                });
            }
            // Close WebSocket connection
            if (this.websocket) {
                this.websocket.close();
                this.websocket = null;
            }
            // Close WebRTC connection
            if (this.webrtcConnection) {
                this.webrtcConnection.close();
                this.webrtcConnection = null;
            }
            // Stop audio streams
            if (this.mediaStream) {
                this.mediaStream.getTracks().forEach((track) => track.stop());
                this.mediaStream = null;
            }
            if (this.audioContext && this.audioContext.state !== 'closed') {
                await this.audioContext.close();
                this.audioContext = null;
            }
            this.session = null;
            logger.info('✅ Cortex session ended');
            return { success: true, data: undefined };
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to end session';
            logger.error('❌ Error ending session:', error);
            return { success: false, error: errorMessage };
        }
    }
    /**
     * Send a text message to the agent
     */
    async sendMessage(text: string): Promise<ServiceResponse<void>> {
        try {
            if (!this.session || !this.session.isActive) {
                return { success: false, error: 'No active session. Please start a session first.' };
            }
            if (!this.isConnected || !this.websocket) {
                return { success: false, error: 'Not connected to agent. Please check connection.' };
            }
            this.sendWebSocketMessage({
                type: 'user_text_input',
                user_text_input: text,
            });
            if (this.session) {
                this.session.messageCount++;
            }
            return { success: true, data: undefined };
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
            return { success: false, error: errorMessage };
        }
    }
    /**
     * Subscribe to events
     */
    on(event: string, handler: (data: unknown) => void): void {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, new Set());
        }
        this.eventListeners.get(event)?.add(handler);
    }
    /**
     * Unsubscribe from events
     */
    off(event: string, handler: (data: unknown) => void): void {
        this.eventListeners.get(event)?.delete(handler);
    }
    /**
     * Emit an event to all listeners
     */
    private emit(event: string, data: unknown): void {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            listeners.forEach((handler) => {
                try {
                    handler(data);
                }
                catch (error) {
                    logger.error(`Error in event handler for ${event}:`, error);
                }
            });
        }
    }
    /**
     * Initialize audio context and media stream
     */
    private async initializeAudio(): Promise<void> {
        try {
            this.audioContext = new (window.AudioContext || (window as unknown as {
                webkitAudioContext: typeof AudioContext;
            }).webkitAudioContext)();
            this.mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
        }
        catch (error) {
            logger.error('❌ Failed to initialize audio:', error);
            throw error;
        }
    }
    /**
     * Initialize voice WebSocket connection
     */
    private async initializeVoiceWebSocket(wsUrl: string, apiKey: string): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                let finalUrl: string;
                if (wsUrl.includes('conversation_signature') || wsUrl.includes('signed_url')) {
                    finalUrl = wsUrl;
                }
                else {
                    finalUrl = wsUrl.includes('?')
                        ? `${wsUrl}&xi-api-key=${apiKey}`
                        : `${wsUrl}?xi-api-key=${apiKey}`;
                }
                this.websocket = new WebSocket(finalUrl);
                this.websocket.onopen = () => {
                    logger.info('✅ WebSocket connected to ElevenLabs');
                    this.isConnected = true;
                    // Send initial conversation trigger
                    setTimeout(() => {
                        this.sendWebSocketMessage({
                            type: 'conversation_initiation_client_data',
                            conversation_config_override: {
                                agent_output_audio_format: 'pcm_16000',
                            },
                        });
                    }, 500);
                    // Start voice streaming
                    this.startVoiceStreaming();
                    resolve();
                };
                this.websocket.onmessage = (event) => {
                    this.handleWebSocketMessage(event);
                };
                this.websocket.onerror = (error) => {
                    logger.error('❌ WebSocket error:', error);
                    this.isConnected = false;
                    reject(error);
                };
                this.websocket.onclose = (event) => {
                    logger.info('🔌 WebSocket closed', { code: event.code, reason: event.reason });
                    this.isConnected = false;
                };
            }
            catch (error) {
                reject(error);
            }
        });
    }
    /**
     * Handle WebSocket messages
     */
    private handleWebSocketMessage(event: MessageEvent): void {
        try {
            const data = JSON.parse(event.data) as Record<string, unknown>;
            // Handle user transcript
            if (data.type === 'user_transcript' && data.user_transcript) {
                const transcript = String(data.user_transcript);
                const isFinal = Boolean(data.is_final);
                if (!isFinal) {
                    // First partial transcript - emit start
                    this.emit('transcription_start', {
                        type: 'transcription_start',
                        role: 'user',
                    } as CortexEvent);
                }
                // Emit update
                this.emit('transcription_update', {
                    type: 'transcription_update',
                    role: 'user',
                    text: transcript,
                    isPartial: !isFinal,
                } as CortexEvent);
                if (isFinal) {
                    // Emit complete
                    this.emit('transcription_complete', {
                        type: 'transcription_complete',
                        role: 'user',
                        text: transcript,
                    } as CortexEvent);
                }
            }
            // Handle agent response
            if (data.type === 'agent_response') {
                if (data.agent_response_audio_delta) {
                    this.handleAudioDelta(String(data.agent_response_audio_delta));
                }
                if (data.agent_response) {
                    const response = String(data.agent_response);
                    const isFinal = Boolean(data.is_final);
                    if (!isFinal) {
                        // First partial response - emit start
                        this.emit('transcription_start', {
                            type: 'transcription_start',
                            role: 'agent',
                        } as CortexEvent);
                    }
                    // Emit update
                    this.emit('transcription_update', {
                        type: 'transcription_update',
                        role: 'agent',
                        text: response,
                        isPartial: !isFinal,
                    } as CortexEvent);
                    if (isFinal) {
                        // Emit complete
                        this.emit('transcription_complete', {
                            type: 'transcription_complete',
                            role: 'agent',
                            text: response,
                        } as CortexEvent);
                    }
                }
            }
            // Handle audio data
            if (data.audio) {
                this.handleAudioDelta(String(data.audio));
            }
            if (this.session) {
                this.session.messageCount++;
            }
        }
        catch (error) {
            logger.error('❌ Failed to parse WebSocket message:', error);
        }
    }
    /**
     * Handle audio delta from agent
     */
    private handleAudioDelta(audioDelta: string): void {
        try {
            const binaryString = atob(audioDelta);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            this.playAudioResponse(bytes.buffer);
        }
        catch (error) {
            logger.error('❌ Failed to handle audio delta:', error);
        }
    }
    /**
     * Start continuous voice streaming
     */
    private async startVoiceStreaming(): Promise<void> {
        if (!this.mediaStream) {
            logger.error('❌ No media stream available');
            return;
        }
        try {
            const mediaRecorder = new MediaRecorder(this.mediaStream, {
                mimeType: 'audio/webm;codecs=opus',
                audioBitsPerSecond: 16000,
            });
            let audioChunks: Blob[] = [];
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunks.push(event.data);
                }
            };
            mediaRecorder.onstop = async () => {
                if (audioChunks.length > 0) {
                    const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
                    const arrayBuffer = await audioBlob.arrayBuffer();
                    await this.sendAudioData(arrayBuffer);
                    audioChunks = [];
                    if (this.isConnected && this.session?.isActive) {
                        mediaRecorder.start(1000);
                    }
                }
            };
            mediaRecorder.start(1000);
        }
        catch (error) {
            logger.error('❌ Failed to start voice streaming:', error);
        }
    }
    /**
     * Send audio data to agent
     */
    private async sendAudioData(audioData: ArrayBuffer): Promise<void> {
        if (!this.session || !this.session.isActive || !this.isConnected || !this.websocket) {
            return;
        }
        try {
            const uint8Array = new Uint8Array(audioData);
            const binaryString = Array.from(uint8Array, (byte) => String.fromCharCode(byte)).join('');
            const base64Audio = btoa(binaryString);
            this.sendWebSocketMessage({
                user_audio_chunk: base64Audio,
            });
        }
        catch (error) {
            logger.error('❌ Failed to send audio data:', error);
        }
    }
    /**
     * Send message via WebSocket
     */
    private sendWebSocketMessage(data: Record<string, unknown>): void {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.send(JSON.stringify(data));
        }
    }
    /**
     * Play audio response
     */
    private async playAudioResponse(audioData: ArrayBuffer): Promise<void> {
        try {
            const audioBlob = new Blob([audioData], { type: 'audio/mpeg' });
            const audioUrl = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioUrl);
            this.emit('audio_level', { output: 0.5 }); // Emit audio level event
            await audio.play();
            audio.onended = () => URL.revokeObjectURL(audioUrl);
        }
        catch (error) {
            logger.error('❌ Failed to play audio response:', error);
        }
    }
}
// Export singleton instance
export const cortexRealtimeAgent = new CortexRealtimeAgentService();
